'use client'

import Link from 'next/link'
import { Icons } from '@/components/ui/icons'
import { formatMoney } from '@/lib/ap/invoices'
import { workbenchLink } from '@/lib/ai/page-context'
import { StatusPill } from './InvoiceCard'
import { useTranslations } from 'use-intl'
import type { ChatAttachment, InvoiceAttachmentData, InvoicesAttachmentData } from '@/lib/ai/types'

/** "invoices" attachment — small table with row deep-links to the workbench. */
export function InvoicesTable({ attachment, onNavigate }: { attachment: ChatAttachment; onNavigate?: () => void }) {
  const t = useTranslations('aiPanel.invoicesTable')
  const data = (attachment.data ?? {}) as InvoicesAttachmentData
  const rows: InvoiceAttachmentData[] = Array.isArray(data.rows) ? data.rows : []
  const total = typeof data.total === 'number' ? data.total : rows.length

  return (
    <div className="overflow-hidden rounded-xl border border-border/50 bg-white shadow-soft">
      <div className="flex items-center justify-between border-b border-border/40 px-3 py-2">
        <p className="text-xs font-semibold text-brand-navy">{attachment.title || t('title')}</p>
        <span className="text-[11px] text-muted-foreground">
          {t('count', { count: total, n: rows.length < total ? t('shownOfTotal', { shown: String(rows.length), total: String(total) }) : String(total) })}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">{attachment.description || t('empty')}</p>
      ) : (
        <div className="max-h-64 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-1.5 font-medium">{t('columns.invoice')}</th>
                <th className="px-3 py-1.5 font-medium">{t('columns.vendor')}</th>
                <th className="px-3 py-1.5 text-right font-medium">{t('columns.amount')}</th>
                <th className="px-3 py-1.5 font-medium">{t('columns.status')}</th>
                <th className="px-2 py-1.5" aria-label={t('open')} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const id = row.invoice_db_id ?? row.id
                const href = typeof id === 'number' ? workbenchLink(id, row.queue ?? data.queue, row.status) : null
                return (
                  <tr key={`${row.invoice_id ?? i}-${i}`} className="border-t border-border/30 hover:bg-brand-cornflower/5">
                    <td className="px-3 py-1.5 font-medium text-brand-navy">
                      {href ? (
                        <Link href={href} onClick={onNavigate} className="hover:underline">{row.invoice_id ?? `#${id}`}</Link>
                      ) : (
                        row.invoice_id ?? '—'
                      )}
                    </td>
                    <td className="max-w-[10rem] truncate px-3 py-1.5 text-foreground">{row.vendor_name ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-1.5 text-right text-foreground">{formatMoney(row.grand_total, row.currency)}</td>
                    <td className="px-3 py-1.5"><StatusPill status={row.status} /></td>
                    <td className="px-2 py-1.5 text-right">
                      {href && (
                        <Link href={href} onClick={onNavigate} className="text-brand-purple" aria-label={t('openInWorkbench', { id: String(row.invoice_id ?? id) })}>
                          <Icons.arrowRight className="inline h-3.5 w-3.5" strokeWidth={1.5} />
                        </Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
